import React, { ChangeEvent, FC, ReactNode, useMemo } from 'react';
import cls from './Select.module.scss';
import { Option, OptionValue, SelectThemes } from './types';
import { ArrowDownIcon, ChevronDown } from 'src/assets/icons/arrows';
import { SearchIcon } from 'src/assets/icons/tools';
import CloseIcon from 'src/assets/icons/mathSigns/CloseIcon';
import Tooltip from '../../components/UI/Tooltip/Tooltip';

interface SelectControllerProps {
  theme: SelectThemes;
  mainOptions: Option[];
  selectedValue: OptionValue;
  selectedValues: OptionValue[];
  isMultiSelect: boolean;
  selectedOptionsAsync: Option[];
  selectedOptionAsync?: Option;
  controllerClassName?: string;
  hasSearch: boolean;
  open: boolean;
  searchValue: string;
  onSearchHandler: (event: ChangeEvent<HTMLInputElement>) => void;
  multiselectRemoveHandler: (
    value: OptionValue,
    event?: React.MouseEvent
  ) => void;
  iconClassname?: string;
  approveNullValue?: boolean;
}
const SelectController: FC<SelectControllerProps> = ({
  theme,
  selectedOptionsAsync,
  selectedOptionAsync,
  selectedValues,
  isMultiSelect,
  selectedValue,
  mainOptions,
  controllerClassName,
  hasSearch,
  open,
  searchValue,
  onSearchHandler,
  multiselectRemoveHandler,
  iconClassname,
  approveNullValue = true,
}) => {
  const selectedOptions = useMemo(() => {
    if (isMultiSelect && Array.isArray(selectedValues)) {
      const res = [...mainOptions];
      selectedOptionsAsync.forEach((item) => {
        if (!res.find((option) => option.value == item.value)) {
          res.push(item);
        }
      });
      return selectedValues.map((value) =>
        res.find((option) => option.value == value)
      ) as Option[];
    }
    return [];
  }, [mainOptions, selectedValues, isMultiSelect, selectedOptionsAsync]);
  const selectedOption = useMemo(() => {
    return (
      selectedOptionAsync ||
      mainOptions.find((item) => item.value == selectedValue)
    );
  }, [selectedValue, selectedOptionAsync, mainOptions]);
  let controllerContent: ReactNode = null;
  if (isMultiSelect) {
    if (selectedOptions.length === 0) {
      controllerContent = <div className={cls['no-value']}>Немає значення</div>;
    } else
      controllerContent = (
        <>
          <Tooltip
            title={selectedOptions
              .map((item) => item?.title || item?.value)
              .join(', ')}
            className={'flex-shrink-1 w-100'}
          >
            <div className={cls['multiselect-wrapper']}>
              {selectedOptions.filter(Boolean).map((item) => (
                <div key={item.value} className={cls['multiselect-item']}>
                  <div
                    className={cls['multiselect-point']}
                    style={
                      item.multiselectPointColor
                        ? { backgroundColor: item.multiselectPointColor }
                        : {}
                    }
                  />
                  {item.controllerOption ? (
                    <>
                      {item.controllerOption?.element}
                      <span
                        className={`${cls['multiselect-item__title']} ${item?.titleClassName || ''}`}
                      >
                        {item.controllerOption?.title}
                      </span>
                    </>
                  ) : (
                    <>
                      {item.element}
                      <span
                        className={`${cls['multiselect-item__title']} ${item?.titleClassName || ''}`}
                      >
                        {item.title}
                      </span>
                    </>
                  )}
                  <CloseIcon
                    className={cls['multiselect-item__close']}
                    onClick={(event) =>
                      multiselectRemoveHandler(item.value, event)
                    }
                  />
                </div>
              ))}
            </div>
          </Tooltip>
        </>
      );
  } else if (hasSearch && open) {
    controllerContent = (
      <>
        <SearchIcon />
        {/*no close нужен для того чтобы мюи поппер который у нас работает в качестве дропдауна не закрывался по нажатию на елемент поиска */}
        <input
          autoFocus
          data-noclose
          className="ml-5 width-full"
          value={searchValue}
          onChange={onSearchHandler}
        />
      </>
    );
  } else if (!selectedOption?.isControllerHidden) {
    if (selectedOption?.controllerOption) {
      controllerContent = (
        <>
          {selectedOption?.controllerOption?.element}
          {selectedOption?.controllerOption?.title && (
            <div
              className={`${cls['controller-title']} ${!selectedOption.value && approveNullValue && cls['null-value']} ${selectedOption.titleClassName || ''}`}
            >
              {selectedOption?.controllerOption.title}
            </div>
          )}
        </>
      );
    } else {
      controllerContent = (
        <>
          {selectedOption?.element}
          {selectedOption?.title && (
            <div
              className={`${cls['controller-title']} ${!selectedOption.value && approveNullValue && cls['null-value']} ${selectedOption.titleClassName || ''}`}
            >
              {selectedOption.title}
            </div>
          )}
        </>
      );
    }
  }
  return (
    <div className={`${cls.controller}  ${controllerClassName}`}>
      {controllerContent}
      {theme === SelectThemes.FROM_TABLE ? (
        <ChevronDown className={`${cls['dropdow-icon']} ${iconClassname}`} />
      ) : (
        <ArrowDownIcon className={`${cls['dropdow-icon']} ${iconClassname}`} />
      )}
    </div>
  );
};

export default SelectController;
